import { useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { useUpdateCompanyUser } from './useUpdateCompanyUser';
import { companyUserService } from '../services/company-user.service';
import type { CompanyRoleName, EditUserData } from '../types/company-admin.types';

export function useEditUserSubmit(
  userId: string,
  originalRole: CompanyRoleName,
  onSuccess: () => void
): { submit: (data: EditUserData) => Promise<void>; isLoading: boolean } {
  const { updateUser, isPending } = useUpdateCompanyUser();
  const [isChangingRole, setIsChangingRole] = useState(false);
  const queryClient = useQueryClient();
  const getEffectiveCompanyId = useAuthStore((state) => state.getEffectiveCompanyId);

  const submit = async (data: EditUserData): Promise<void> => {
    try {
      setIsChangingRole(true);
      await updateUser(
        {
          userId,
          data: {
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone || undefined,
          },
        },
        {
          onError: () => {
            throw new Error('Failed to update profile');
          },
        }
      );
      if (data.role !== originalRole) {
        await companyUserService.removeRole(userId, originalRole);
        await companyUserService.assignRole(userId, data.role);
        queryClient.invalidateQueries({ queryKey: ['company-users', getEffectiveCompanyId()] });
      }
      toast.success('User updated successfully');
      onSuccess();
    } catch {
      toast.error('Failed to update user');
    } finally {
      setIsChangingRole(false);
    }
  };

  return { submit, isLoading: isPending || isChangingRole };
}
